import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileDown, RotateCcw, CheckCircle2, XCircle } from "lucide-react";

const ResultsSection = ({ result, onExportPDF, onNewAssessment }) => {
  const isPermitted = result.score.classification === "permitted";

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-[#3b444d] mb-4">
          Assessment Complete
        </h2>
        {result.nutrients.product_name && (
          <p className="text-xl text-gray-600">
            {result.nutrients.product_name}
          </p>
        )}
      </div>

      {/* Important Disclaimer */}
      <Card className="shadow-xl border-4 border-amber-400 bg-amber-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <svg className="w-8 h-8 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-amber-900 mb-2">Guidance Tool Only - Not Legal Advice</h3>
              <p className="text-amber-800 mb-3">
                This tool provides indicative guidance based on the UK nutrient profiling model. The results should be used as an initial assessment only and <strong>do not constitute legal advice or a definitive determination</strong>.
              </p>
              <p className="text-amber-800 font-semibold">
                For more comprehensive guidance and confirmation of your product's advertising compliance status, please contact the IPM Advisory Service.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Classification Result */}
      <Card
        className={`shadow-2xl border-4 ${
          isPermitted ? "border-[#cbcb3c] bg-green-50" : "border-[#e83b84] bg-red-50"
        }`}
        data-testid="classification-result"
      >
        <CardContent className="p-12">
          <div className="flex flex-col items-center space-y-6">
            {isPermitted ? (
              <CheckCircle2 className="w-24 h-24 text-[#cbcb3c]" data-testid="permitted-icon" />
            ) : (
              <XCircle className="w-24 h-24 text-[#e83b84]" data-testid="restricted-icon" />
            )}
            <h3
              className={`text-4xl font-bold ${
                isPermitted ? "text-[#cbcb3c]" : "text-[#e83b84]"
              }`}
              data-testid="classification-status"
            >
              {isPermitted ? "✅ APPEARS PERMITTED" : "❌ APPEARS RESTRICTED"}
            </h3>
            <p className="text-xl text-gray-700 max-w-3xl text-center" data-testid="classification-explanation">
              Based on the nutrient profiling score, it <strong>appears</strong> that {result.score.explanation.toLowerCase()}
            </p>

            {/* Advertising Restrictions Guidance */}
            <div className={`mt-8 p-6 rounded-xl w-full max-w-3xl ${
              isPermitted ? "bg-white" : "bg-white"
            }`}>
              <h4 className="text-xl font-bold text-[#3b444d] mb-4">
                Indicative UK Advertising Guidance
              </h4>
              
              {isPermitted ? (
                <div className="space-y-3 text-left">
                  <p className="text-gray-700">
                    <span className="font-semibold text-[#cbcb3c]">✓</span> Based on this assessment, this product <strong>appears to be permitted</strong> for advertising without the restrictions that apply to less healthy products.
                  </p>
                  <p className="text-gray-600 text-sm">
                    Products scoring below the threshold may not be subject to the advertising restrictions that came into force on 5 January 2026. However, <strong>this is indicative only</strong> and specific product circumstances may affect compliance.
                  </p>
                  <div className="mt-4 p-4 bg-blue-50 border-l-4 border-[#64c2c8] rounded">
                    <p className="text-sm text-gray-700">
                      <strong>Important:</strong> Product formulations, serving sizes, and category classifications can affect advertising compliance. Contact IPM Advisory Service for definitive guidance on your specific product.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 text-left">
                  <div className="p-4 bg-red-50 rounded-lg border-l-4 border-[#e83b84]">
                    <p className="font-semibold text-gray-800 mb-2">
                      Based on this assessment, this product <strong>appears likely to be subject</strong> to the following advertising restrictions:
                    </p>
                    <ul className="space-y-2 text-gray-700 ml-4">
                      <li className="flex items-start">
                        <span className="mr-2 text-[#e83b84]">•</span>
                        <span><span className="font-semibold">TV Advertising:</span> Likely restricted before 9pm watershed on broadcast TV and all on-demand programme services (ODPS) under UK jurisdiction</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 text-[#e83b84]">•</span>
                        <span><span className="font-semibold">Online Advertising:</span> Likely subject to 24-hour restriction on paid-for advertising online, including non-Ofcom regulated ODPS</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-2">
                    <p>
                      <span className="font-semibold">Effective Date:</span> These restrictions came into force on 5 January 2026 under the Health and Care Act 2022.
                    </p>
                    <p>
                      <span className="font-semibold">Applies to:</span> Businesses with 250 or more employees (including franchises and symbol groups) involved in manufacturing or selling food/drink products.
                    </p>
                  </div>

                  <div className="mt-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded">
                    <p className="text-sm text-gray-700">
                      <strong>Important:</strong> Certain product categories have specific exemptions or modified thresholds. Some products may be out of scope entirely. Contact IPM Advisory Service for a comprehensive review of your product's compliance status.
                    </p>
                  </div>
                  
                  <div className="pt-2 border-t border-gray-300">
                    <p className="text-xs text-gray-600">
                      <span className="font-semibold">Regulatory Context:</span> The restrictions apply to "identifiable" products within advertisements. Implementation details and enforcement are overseen by the ASA (Advertising Standards Authority).
                    </p>
                  </div>
                  
                  <a
                    href="https://www.gov.uk/government/publications/restricting-advertising-of-less-healthy-food-or-drink-on-tv-and-online-products-in-scope"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 text-sm text-[#64c2c8] hover:text-[#3b444d] font-semibold underline"
                  >
                    View full UK government guidance →
                  </a>
                </div>
              )}
            </div>

            {/* IPM Advisory CTA */}
            <div className="mt-6 p-6 bg-gradient-to-r from-[#3b444d] to-[#64c2c8] rounded-xl w-full max-w-3xl text-white">
              <div className="text-center">
                <h4 className="text-2xl font-bold mb-3">Need Definitive Guidance?</h4>
                <p className="text-lg mb-4 opacity-95">
                  Contact the IPM Advisory Service for comprehensive guidance tailored to your specific product and circumstances.
                </p>
                <p className="text-sm opacity-90 mb-4">
                  Our expert team can provide detailed compliance reviews, category-specific guidance, and support with regulatory submissions.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href="mailto:legal@ipm.org.uk"
                    className="inline-block px-6 py-3 bg-white text-[#3b444d] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    📧 legal@ipm.org.uk
                  </a>
                  <a
                    href="https://theipm.org.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-3 bg-[#e83b84] text-white font-semibold rounded-lg hover:bg-[#d02f73] transition-colors"
                  >
                    Visit IPM Website
                  </a>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Score Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* A Points */}
        <Card className="shadow-xl border-0" data-testid="a-points-card">
          <CardContent className="p-6 bg-red-50">
            <h4 className="text-xl font-bold text-[#e83b84] mb-4">
              A Points (Negative)
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Energy:</span>
                <span className="font-bold" data-testid="a-points-energy">
                  {result.score.a_points_energy}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Saturated Fat:</span>
                <span className="font-bold" data-testid="a-points-sat-fat">
                  {result.score.a_points_sat_fat}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Free Sugars:</span>
                <span className="font-bold" data-testid="a-points-sugars">
                  {result.score.a_points_sugars}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Salt:</span>
                <span className="font-bold" data-testid="a-points-salt">
                  {result.score.a_points_sodium}
                </span>
              </div>
              <div className="pt-3 border-t-2 border-red-200 flex justify-between text-lg">
                <span className="font-bold text-[#e83b84]">Total A:</span>
                <span className="font-bold text-[#e83b84]" data-testid="total-a-points">
                  {result.score.total_a_points}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* C Points */}
        <Card className="shadow-xl border-0" data-testid="c-points-card">
          <CardContent className="p-6 bg-green-50">
            <h4 className="text-xl font-bold text-[#cbcb3c] mb-4">
              C Points (Positive)
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Fruit/Veg/Nut/Seeds:</span>
                <span className="font-bold" data-testid="c-points-fvns">
                  {result.score.c_points_fvn}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Fibre (AOAC):</span>
                <span className="font-bold" data-testid="c-points-fibre">
                  {result.score.c_points_fibre}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Protein:</span>
                <span className="font-bold" data-testid="c-points-protein">
                  {result.score.c_points_protein}
                  {!result.score.protein_points_applied && (
                    <span className="text-xs text-gray-500 ml-1">(excluded)</span>
                  )}
                </span>
              </div>
              {!result.score.protein_points_applied && (
                <p className="text-xs text-gray-600 pt-2">
                  * Protein points not applied (A ≥ 11 and FVN &lt; 5)
                </p>
              )}
              <div className="pt-3 border-t-2 border-green-200 flex justify-between text-lg">
                <span className="font-bold text-[#cbcb3c]">Total C:</span>
                <span className="font-bold text-[#cbcb3c]" data-testid="total-c-points">
                  {result.score.total_c_points}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Final Score */}
        <Card className="shadow-xl border-0" data-testid="final-score-card">
          <CardContent className="p-6 bg-[#64c2c8] text-white">
            <h4 className="text-xl font-bold mb-4">Final Score</h4>
            <div className="space-y-3">
              <div className="flex justify-between text-lg">
                <span>A Points:</span>
                <span className="font-bold">{result.score.total_a_points}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span>C Points:</span>
                <span className="font-bold">-{result.score.total_c_points}</span>
              </div>
              <div className="pt-3 border-t-2 border-white/30 flex justify-between">
                <span className="text-2xl font-bold">Score:</span>
                <span className="text-3xl font-bold" data-testid="final-score">
                  {result.score.final_score}
                </span>
              </div>
              <div className="pt-3 border-t-2 border-white/30">
                <p className="text-sm opacity-90" data-testid="threshold-info">
                  Threshold for {result.nutrients.product_type}s: ≥
                  {result.score.threshold}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Nutritional Details */}
      <Card className="shadow-xl border-0" data-testid="nutritional-details-card">
        <CardContent className="p-8">
          <h3 className="text-2xl font-bold text-[#3b444d] mb-6">
            Nutritional Information (per 100g) - NPM 2018
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-gray-600 text-sm">Energy</p>
              <p className="text-lg font-bold" data-testid="nutrient-energy">
                {result.nutrients.energy_kj} kJ
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Saturated Fat</p>
              <p className="text-lg font-bold" data-testid="nutrient-sat-fat">
                {result.nutrients.saturated_fat_g} g
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Free Sugars</p>
              <p className="text-lg font-bold" data-testid="nutrient-sugars">
                {result.nutrients.free_sugars_g} g
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Salt</p>
              <p className="text-lg font-bold" data-testid="nutrient-salt">
                {result.nutrients.salt_g} g
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Fruit/Veg/Nut/Seeds</p>
              <p className="text-lg font-bold" data-testid="nutrient-fvns">
                {result.nutrients.fruit_veg_nut_seeds_percent}%
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">
                Fibre (AOAC)
              </p>
              <p className="text-lg font-bold" data-testid="nutrient-fibre">
                {result.nutrients.fibre_g} g
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Protein</p>
              <p className="text-lg font-bold" data-testid="nutrient-protein">
                {result.nutrients.protein_g} g
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-center">
        <Button
          onClick={onExportPDF}
          className="h-14 px-8 bg-[#3b444d] hover:bg-[#2a3139] text-white font-semibold"
          data-testid="export-pdf-button"
        >
          <FileDown className="w-5 h-5 mr-2" />
          Export PDF
        </Button>
        <Button
          onClick={onNewAssessment}
          variant="outline"
          className="h-14 px-8 border-2 border-[#3b444d] text-[#3b444d] hover:bg-gray-100 font-semibold"
          data-testid="new-assessment-button"
        >
          <RotateCcw className="w-5 h-5 mr-2" />
          New Assessment
        </Button>
      </div>
    </div>
  );
};

export default ResultsSection;
